import React from 'react';
import PropTypes from 'prop-types';
import { Button, Table } from 'react-bootstrap';
import Loader from '../../assets/images/loader.gif';
import { TENANT_STATUS } from '../../utils/tenantMappers';
import { AppContext } from '../../context/appContext';

const getStatusBadgeClass = (status) =>
  status === TENANT_STATUS.ACTIVE ? 'bg-success' : 'bg-warning text-dark';

const TenantTable = ({
  tenants,
  isLoading,
  onEdit,
  onChangeStatus,
  onRefresh,
}) => {
  const { profile } = React.useContext(AppContext);
  const { user } = profile || {};

  const handleToggleStatus = (tenant) => {
    const nextStatus =
      tenant.status === TENANT_STATUS.ACTIVE
        ? TENANT_STATUS.SUSPENDED
        : TENANT_STATUS.ACTIVE;

    // eslint-disable-next-line no-alert
    const confirmed = window.confirm(
      `Are you sure you want to set ${tenant.name} to ${nextStatus}?`,
    );

    if (confirmed) {
      onChangeStatus(tenant, nextStatus);
    }
  };

  return (
    <div className="prfList">
      <div className="prfListHead">
        <div className="prfListTitle pageHead d-flex justify-content-between align-items-center">
          <div>
            <h2>Tenants</h2>
            <p>Manage tenant metadata and status lifecycle.</p>
          </div>
          <Button
            variant="outline-primary"
            onClick={onRefresh}
            aria-label="refresh-tenants"
          >
            Refresh
          </Button>
        </div>
      </div>

      <div className="tabelHolder tenantsTable adminUserList">
        <Table bordered striped hover responsive className="m-0">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Subdomain</th>
              <th>Business Domain</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td
                  colSpan={6}
                  style={{ height: '240px', textAlign: 'center' }}
                >
                  <img src={Loader} width={50} height={50} alt="loading" />
                </td>
              </tr>
            ) : tenants.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center bg-white">
                  No tenants found.
                </td>
              </tr>
            ) : (
              tenants.map((tenant) => (
                <tr key={tenant.id}>
                  <td>{tenant.name}</td>
                  <td>{tenant.email}</td>
                  <td>{tenant.subdomain}</td>
                  <td>{tenant.business_domain || '------'}</td>
                  <td>
                    <span
                      className={`badge ${getStatusBadgeClass(tenant.status)}`}
                    >
                      {tenant.status}
                    </span>
                  </td>
                  <td>
                    {tenant.id !== user.tenant_id && (
                      <div className="d-flex gap-2">
                        <Button
                          size="sm"
                          variant="primary"
                          onClick={() => onEdit(tenant)}
                          aria-label={`edit-tenant-${tenant.id}`}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <g clipPath="url(#clip0_1160_2199)">
                              <path
                                d="M9.37333 6.01333L9.98667 6.62667L3.94667 12.6667H3.33333V12.0533L9.37333 6.01333ZM11.7733 2C11.6067 2 11.4333 2.06667 11.3067 2.19333L10.0867 3.41333L12.5867 5.91333L13.8067 4.69333C14.0667 4.43333 14.0667 4.01333 13.8067 3.75333L12.2467 2.19333C12.1133 2.06 11.9467 2 11.7733 2ZM9.37333 4.12667L2 11.5V14H4.5L11.8733 6.62667L9.37333 4.12667Z"
                                fill="white"
                              />
                            </g>
                            <defs>
                              <clipPath id="clip0_1160_2199">
                                <rect width="16" height="16" fill="white" />
                              </clipPath>
                            </defs>
                          </svg>
                        </Button>
                        <Button
                          title={
                            tenant.status === TENANT_STATUS.ACTIVE
                              ? 'Suspend Tenant'
                              : 'Activate Tenant'
                          }
                          size="sm"
                          variant={
                            tenant.status === TENANT_STATUS.ACTIVE
                              ? 'btn btn-danger'
                              : 'btn btn-success'
                          }
                          onClick={() => handleToggleStatus(tenant)}
                          aria-label={`toggle-status-${tenant.id}`}
                        >
                          {tenant.status === TENANT_STATUS.ACTIVE ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              height="1em"
                              viewBox="0 0 448 512"
                            >
                              <path
                                fill="white"
                                d="M135.2 17.7L128 32H32C14.3 32 0 46.3 0 64S14.3 96 32 96H416c17.7 0 32-14.3 32-32s-14.3-32-32-32H320l-7.2-14.3C307.4 6.8 296.3 0 284.2 0H163.8c-12.1 0-23.2 6.8-28.6 17.7zM416 128H32L53.2 467c1.6 25.3 22.6 45 47.9 45H346.9c25.3 0 46.3-19.7 47.9-45L416 128z"
                              />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="25"
                              height="25"
                              fill="currentColor"
                              className="bi bi-check"
                              viewBox="0 0 16 16"
                            >
                              <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z" />
                            </svg>
                          )}
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

TenantTable.propTypes = {
  tenants: PropTypes.array,
  isLoading: PropTypes.bool,
  onEdit: PropTypes.func,
  onChangeStatus: PropTypes.func,
  onRefresh: PropTypes.func,
};

export default TenantTable;
